import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createCenterOlComment } from '../../../../api/centers/center-ol-comments/create-center-ol-comment'
import { deleteCenterOlComments } from '../../../../api/centers/center-ol-comments/delete-center-ol-comment'
import { getCenterOlComments } from '../../../../api/centers/center-ol-comments/get-center-ol-comments'
import { updateCenterOlComment } from '../../../../api/centers/center-ol-comments/update-center-ol-comment'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { CenterOlComment } from '../../../../lib/types/models/center-ol-comment/center-ol-comment'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import OlPersonalSpaceCenterOlCommentForm from './ol-personal-space-center-ol-comment-form'

export type Props = {
  id: number
}

const OlPersonalSpaceCommentSection = ({ id }: Props) => {
  const queryClient = useQueryClient()

  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    CenterOlComment
  >(['create', 'edit', 'delete'])

  const { data: centerOlComments } = useQuery({
    queryKey: ['center-ol-comments'],
    queryFn: () => getCenterOlComments({ centerId: id })
  })

  const deleteComment = useMutation({
    mutationFn: deleteCenterOlComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment deleted successfully')
      queryClient.invalidateQueries({
        queryKey: ['center-ol-comments']
      })
      closeModel('delete')
    }
  })

  const createComment = useMutation({
    mutationFn: createCenterOlComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment added successfully')
      queryClient.invalidateQueries({
        queryKey: ['center-ol-comments']
      })
      closeModel('create')
    }
  })

  const updateComment = useMutation({
    mutationFn: updateCenterOlComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Comment updated successfully')
      queryClient.invalidateQueries({
        queryKey: ['center-ol-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<CenterOlComment>[] = [
    {
      enableSorting: true,
      header: 'Comments',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography
          variant="tag"
          color={tailwindColors.black.DEFAULT}
          className="text-wrap px-3 py-4"
        >
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Date & time',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: false,
      header: '',
      columnHeaderClassName: 'w-12',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2 pr-3'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  if (!centerOlComments) return <Loader center />

  return (
    <InnerBox className=" rounded-lg border border-grey-light p-0">
      <ActionTableWrapper
        title={'Local organiser comments'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Add comment'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<CenterOlComment>
          manualSorting={false}
          bodyVariant="white"
          columns={columns}
          data={centerOlComments.data ?? []}
          rowCount={centerOlComments?.data.length ?? 0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Add a comment'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <OlPersonalSpaceCenterOlCommentForm
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                await createComment.mutateAsync({ ...values, centerId: id })
              },
              isEnglish: true
            })}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Edit comment'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <OlPersonalSpaceCenterOlCommentForm
              defaultValues={{ comment: selectedData.comment }}
              updatedAt={selectedData.updatedAt}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  await updateComment.mutateAsync({ ...values, id: selectedData.id, centerId: id })
                },
                isEnglish: true
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Delete',
              cancelText: 'Cancel',
              onConfirm: async () => {
                await deleteComment.mutateAsync({ centerId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Are you sure you want to delete this comment?'}
            icon={DeleteIcon}
            isLoading={deleteComment.isPending}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </InnerBox>
  )
}

export default OlPersonalSpaceCommentSection
